import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ExportType } from '@app/home/features/export-gasx/models/export-type.enum';
import { ImportReport } from '@app/home/features/topology/models/manual-override.model';
import { Ouvrages } from '@app/home/features/topology/models/ouvrages.model';
import { SimulatedEquipment } from '@app/home/features/topology/models/simulatedEquipment.model';
import { ManualGasXTransfert } from '@core/models/manual-gasx-transfert.model';
import { SimulatedProperties } from '@core/models/simulated-properties.model';
import { SimulationMetadata, SimulationType } from '@core/models/simulation-metadata.model';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SimulationService {
  private http = inject(HttpClient);

  private baseUrl: string = environment.apiUrl + '/simulation';

  public getSimulatedProperties(): Observable<SimulatedProperties> {
    return this.http.get<SimulatedProperties>(`${this.baseUrl}/simulatedbynode`);
  }

  public getSimulationMetadata(): Observable<Array<SimulationMetadata>> {
    return this.http.get<Array<SimulationMetadata>>(`${this.baseUrl}/metadata`);
  }

  public runRecompute(
    user: string,
    startInterval: string,
    endInterval: string,
    simulationType: SimulationType,
    exportGasX?: boolean,
    isEndless?: boolean
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/recompute`, {
      user,
      startInterval,
      endInterval,
      simulationType,
      exportGasX: simulationType === SimulationType.MREKO ? exportGasX : undefined,
      isAuto: false,
      isEndless
    });
  }

  public runManualGasXTransfert(
    user: string,
    startInterval: string,
    endInterval: string,
    exportTypes: Array<ExportType>,
    keys: Array<string>
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/manual-gasx-transfert`, {
      user,
      startInterval,
      endInterval,
      exportTypes,
      keys
    });
  }

  public cancelSimulation(simulationType: SimulationType, user: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/cancel/${simulationType}/${user}`, null);
  }

  public getSimulationResult(simulationType: SimulationType, date: string, topologyId: number): Observable<Blob> {
    return this.http.get<Blob>(`${this.baseUrl}/results/${simulationType}/${date}/${topologyId}`, {
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }

  public getSimulationGasBlock(simulationType: SimulationType, date: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.baseUrl}/gas-block/${simulationType}/${date}`, {
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }

  public getSimulationEngine(simulationType: SimulationType, date: string): Observable<Blob> {
    return this.http.get<Blob>(`${this.baseUrl}/engine/${simulationType}/${date}`, {
      observe: 'body',
      responseType: 'blob' as 'json'
    });
  }

  public getSimulationOuvrages(): Observable<Ouvrages> {
    return this.http.get<Ouvrages>(`${this.baseUrl}/ouvrages`);
  }

  public getSimulatedEquipments(): Observable<Array<SimulatedEquipment>> {
    return this.http.get<Array<SimulatedEquipment>>(`${this.baseUrl}/simulated`);
  }

  public getActiveEquipments(): Observable<Array<SimulatedEquipment>> {
    return this.http.get<Array<SimulatedEquipment>>(`${this.baseUrl}/equipments`);
  }

  public getManualGasXTransfert(): Observable<ManualGasXTransfert> {
    return this.http.get<ManualGasXTransfert>(`${this.baseUrl}/manual-gasx-transfert`);
  }

  public cancelManualGasXTransfert(user: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/cancel-manual-gasx-transfert/${user}`, null);
  }

  public exportSimulation(simulationType: SimulationType, date: string, topologyId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/export/${simulationType}/${date}/${topologyId}`, { responseType: 'blob' });
  }

  public importSimulation(importFile: File): Observable<ImportReport> {
    const formData = new FormData();
    formData.append('simulationFile', importFile, importFile.name);

    return this.http.post<ImportReport>(`${this.baseUrl}/import`, formData);
  }
}
